import { useSelector } from 'react-redux'
import { get } from 'lodash'
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { Home, UserRole } from '@lmc/ts-common'
import { ReportSidebarNavigation } from './components'
import { getNavigationTabs } from './utils'
import { isBrowser, isTablet } from 'react-device-detect'
import styled from 'styled-components'
import { FC } from 'react'
import { getJSON } from 'common/services/api'
import { useQuery } from 'react-query'
import { CustomMenuItemType } from 'utils'
import { useReportsSidebarNavigation } from './components/utils'
import useFeatureFlags from 'hooks/useFeatureFlags'
import { useRoleAccess } from 'components/AccessControl'
import { Spinner } from 'components/generic'

const StyledContainer = styled.div`
  display: flex;
  height: calc(100vh - 64px);
  max-width: 2000px;
  margin-left: auto;
  margin-right: auto;
`

const StyledRightContainer = styled.div`
  max-height: calc(100vh - 64px);
  overflow-y: auto;
  width: 100%;
`

const ReportView: FC = () => {
  const role = useSelector((state) => get(state, 'global.user.role')) || ''
  const isPrivacyOfficerView = useSelector((state) =>
    get(state, 'global.user.privacyOfficer')
  )

  const { data: flags, isLoading: isFlagsLoading } = useFeatureFlags({
    flagNames: ['incident_reports_list_view', 'incidents_dashboard', 'fluid_tracking_monitoring_report'],
  })
  const downloadCentreEnabled = (role as UserRole) !== UserRole.seniorCarer

  const incidentReportsListViewEnabled =
    flags.incident_reports_list_view.enabled

  const incidentsDashboardEnabled = flags.incidents_dashboard.enabled

  const fluidTrackingMonitoringReportEnabled = flags.fluid_tracking_monitoring_report.enabled

  const homeId = useSelector(
    (state: { global: { home: Home } }) => state?.global?.home?.id
  )

  const { data } = useQuery<{ result: Home }>(['home', homeId], () =>
    getJSON({ url: `/api/list/homes/${homeId}` })
  )

  const features = data?.result

  const carePlanNominative = useSelector(
    (state: { global: { home: Home } }) =>
      state.global.home.preferredCarePlansNominative
  )

  const location = useLocation()

  const navigate = useNavigate()

  const { defaultOpenKey, defaultSelectedKey, exceptionWidthRoutes } =
    useReportsSidebarNavigation()

  const hasRolePermissionForDownloadCentre = useRoleAccess(
    [UserRole.careHomeAdmin],
    [
      {
        i: 'view',
        this: 'Report',
      },
    ]
  )

  return (
    <StyledContainer>
      {isDesktop && (
        <ReportSidebarNavigation
          menuItems={
            getNavigationTabs(
              features,
              role,
              carePlanNominative,
              downloadCentreEnabled,
              isPrivacyOfficerView,
              incidentReportsListViewEnabled,
              incidentsDashboardEnabled,
              hasRolePermissionForDownloadCentre,
            ) as CustomMenuItemType[]
          }
          navigate={navigate}
          defaultOpenKey={defaultOpenKey}
          defaultSelectedKey={defaultSelectedKey}
        />
      )}
      <StyledRightContainer>
        <div
          style={{
            marginRight: 'auto',
            marginLeft: 'auto',
            maxWidth: exceptionWidthRoutes ? 'inherit' : 1128,
          }}
        >
          {isFlagsLoading ? <Spinner /> : (
            location.pathname === '/reports' ? (
              <Navigate to={fluidTrackingMonitoringReportEnabled ? 'monitoring/fluids' : 'coronavirus'} />
            ) : (
              <Outlet />
            )
          )}
        </div>
      </StyledRightContainer>
    </StyledContainer>
  )
}

const isDesktop = isBrowser || isTablet

export default ReportView
